import { useEffect, useRef, useState } from 'react';
import {
  deleteFavorite,
  getAnalysisHistoryItem,
  runMatchAnalysis,
  runDecisionAi,
  saveFavorite,
  type AnalysisPayloadDto,
  type DecisionAiDto,
} from '../api';
import { formatKickoff } from '../lib/format';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';

export interface AnalysisTarget {
  scheduleId: string;
  homeTeamName: string;
  awayTeamName: string;
  kickoffTime: string;
  lotteryNumber?: string | null;
}

const AUDIT_LABELS: Record<string, string> = {
  ok: '通过', warn: '降级', fail: '缺失',
};
const DIRECTION_LABELS: Record<string, string> = {
  over: '偏大', under: '偏小', neutral: '中性',
  home_cover: '主队让球方向', away_cover: '客队让球方向', missing: '让球缺失',
  home: '主胜', draw: '平', away: '客胜', double: '双选倾向',
};
const HALF_LABELS: Record<string, string> = { home: '主队领先', draw: '平局', away: '客队领先' };

function Stars({ value }: { value: number }) {
  return <span className="analysis-stars" aria-label={`${value} 星`}>{'★'.repeat(value)}<i>{'★'.repeat(5 - value)}</i></span>;
}

function AuditStrip({ data }: { data: AnalysisPayloadDto }) {
  return <section className={`analysis-audit audit-${data.analysis.audit.overall}`}>
    <div className="analysis-section-title"><span>数据体检</span><b>{data.analysis.audit.overall === 'ok' ? '数据完整' : data.analysis.audit.overall === 'warn' ? '部分降级' : '数据不足'}</b></div>
    <div className="audit-grid">{data.analysis.audit.checks.map((check) => <div key={check.key} className={`audit-item audit-${check.status}`}><i>{AUDIT_LABELS[check.status] ?? check.status}</i><span>{check.message}</span></div>)}</div>
  </section>;
}

function RuleLayer({ title, value, children, defaultOpen = false }: { title: string; value: string; children: React.ReactNode; defaultOpen?: boolean }) {
  return <details className="rule-layer" open={defaultOpen}><summary><span>{title}</span><b>{value}</b><i>展开依据</i></summary><div className="rule-layer-body">{children}</div></details>;
}

function RuleColumn({ data }: { data: AnalysisPayloadDto }) {
  const rule = data.analysis.rule;
  const oneX2 = rule.oneX2;
  return <section className="analysis-column rule-column">
    <div className="analysis-column-head"><span>规则判向</span><small>{data.analysis.ruleVersion}</small></div>
    <RuleLayer title="第一层 · 大小球" value={DIRECTION_LABELS[rule.totals.direction] ?? rule.totals.direction} defaultOpen>
      <div className="analysis-kv"><span>盘线</span><b>{rule.totals.line ?? '—'}</b><span>即时水位</span><b>{rule.totals.overWater ?? '—'} / {rule.totals.underWater ?? '—'}</b><span>信号来源</span><b>{rule.totals.source}</b></div>
      <ul>{rule.totals.reasons.map((item) => <li key={item}>{item}</li>)}</ul>
    </RuleLayer>
    <RuleLayer title="第二层 · 让球盘" value={DIRECTION_LABELS[rule.handicap.direction] ?? rule.handicap.direction}>
      <div className="analysis-kv"><span>公司</span><b>{rule.handicap.provider ?? '—'}</b><span>盘线</span><b>{rule.handicap.lineText ?? rule.handicap.lineValue ?? '—'}</b><span>双向水位</span><b>{rule.handicap.homeWater ?? '—'} / {rule.handicap.awayWater ?? '—'}</b></div>
      <ul>{rule.handicap.reasons.map((item) => <li key={item}>{item}</li>)}</ul>
    </RuleLayer>
    <RuleLayer title="第三层 · 胜平负" value={DIRECTION_LABELS[oneX2.pick ?? ''] ?? '暂无'}>
      <div className="probability-bars">{(['home', 'draw', 'away'] as const).map((side) => <div key={side}><span>{DIRECTION_LABELS[side]}</span><i style={{ width: `${Math.round(oneX2.probabilities[side] * 100)}%` }} /><b>{Math.round(oneX2.probabilities[side] * 100)}%</b></div>)}</div>
      <p className="muted">来源：{oneX2.sources.join(' + ') || '无'}</p>
    </RuleLayer>
    <RuleLayer title="第四层 · 比分与半全场" value={`${rule.score.topScore ?? '—'} / ${HALF_LABELS[rule.score.halfTimePick ?? ''] ?? '—'}`}>
      <div className="score-pool">{rule.score.pool.map((row, index) => <div key={row.label} className={index === 0 ? 'top' : ''}><b>{row.label}</b><span>{(row.probability * 100).toFixed(1)}%</span></div>)}</div>
      <div className="analysis-kv"><span>预期进球</span><b>{rule.score.expectedHomeGoals} - {rule.score.expectedAwayGoals}</b><span>半场</span><b>{HALF_LABELS[rule.score.halfTimePick ?? ''] ?? '—'}</b><span>半场来源</span><b>{rule.score.halfTimeModeled ? '模型推算' : '历史半场数据'}</b></div>
      {rule.score.constraints.map((item) => <p className="muted" key={item}>{item}</p>)}
    </RuleLayer>
  </section>;
}

function AiColumn({ data, result, loading, error, onRun }: { data: AnalysisPayloadDto; result: DecisionAiDto | null; loading: boolean; error: string; onRun: () => void }) {
  return <section className="analysis-column ai-column">
    <div className="analysis-column-head"><span>AI 融合解读</span><small>{result?.model ?? (data.ai.available ? '等待模型' : '规则摘要')}</small></div>
    {!data.ai.available && <div className="analysis-empty">未配置或未启用大模型。规则判向仍可正常使用，最终建议以规则为准。</div>}
    {data.ai.available && !result && !loading && !error && <button className="primary wide" onClick={onRun}>生成 AI 解读</button>}
    {loading && <div className="analysis-loading"><i className="spinner" />正在读取规则数据并生成融合解读…</div>}
    {error && <div className="login-error">{error}</div>}
    {result && <div className={`ai-report ${result.warning ? 'warning' : ''}`}>
      <div className="ai-report-meta"><span>{result.source}</span><span>{result.cached ? '缓存结果' : '刚刚生成'}</span>{typeof result.structured?.agreesWithRule === 'boolean' && <b className={result.structured.agreesWithRule ? 'agree' : 'disagree'}>{result.structured.agreesWithRule ? '与规则一致' : '与规则分歧'}</b>}</div>
      {result.structured ? <>
        <p className="ai-summary">{result.structured.summary}</p>
        <h4>关键依据</h4><ul>{(result.structured.keyReasons ?? []).map((item) => <li key={item}>{item}</li>)}</ul>
        <h4>风险点</h4><ul>{(result.structured.risks ?? []).map((item) => <li key={item}>{item}</li>)}</ul>
        <h4>数据缺口</h4><ul>{(result.structured.dataGaps ?? []).length ? result.structured.dataGaps!.map((item) => <li key={item}>{item}</li>) : <li>无显式缺口</li>}</ul>
      </> : <pre>{result.analysis}</pre>}
      {result.warning && <small>降级原因：{result.warning}</small>}
    </div>}
    <div className="ai-authority-note">规则引擎拥有最终建议权；模型分歧不会覆盖最终结论。</div>
  </section>;
}

function Dimensions({ data }: { data: AnalysisPayloadDto }) {
  return <section className="dimension-section"><div className="analysis-section-title"><span>六维深度</span><b>{data.analysis.dimensions.filter((item) => item.status === 'ok').length}/6 可用</b></div><div className="dimension-grid">{data.analysis.dimensions.map((item) => <article key={item.key} className={`dimension-card dim-${item.status}`}><div><b>{item.label}</b><span>{item.status === 'ok' ? '可用' : item.status === 'partial' ? '部分' : '不足'}</span></div><p>{item.summary}</p><ul>{item.evidence.slice(0, 4).map((line) => <li key={line}>{line}</li>)}</ul></article>)}</div></section>;
}

function References({ data }: { data: AnalysisPayloadDto }) {
  const rows = [...data.analysis.references.home.map((row) => ({ ...row, side: '主队库' })), ...data.analysis.references.away.map((row) => ({ ...row, side: '客队库' }))];
  if (rows.length === 0) return <section className="dimension-section"><div className="analysis-section-title"><span>历史参照</span><b>暂无</b></div><div className="analysis-empty">没有通过严格门禁的历史参照场。</div></section>;
  return <section className="dimension-section"><div className="analysis-section-title"><span>历史参照</span><b>{rows.length} 场</b></div><div className="reference-table"><div className="reference-row head"><span>库</span><span>比赛</span><span>盘线</span><span>水位拟合</span><span>实际</span></div>{rows.map((row) => <div className="reference-row" key={row.side + row.scheduleId}><span>{row.side}</span><span>{row.homeTeamName} {row.homeGoals}:{row.awayGoals} {row.awayTeamName}</span><span>{row.lineOpen}→{row.lineClose}</span><span>{row.waterFit.toFixed(2)}</span><span className={`res-${row.resultDirection}`}>{row.resultDirection === 'over' ? '大' : row.resultDirection === 'under' ? '小' : '走'}</span></div>)}</div></section>;
}

function AnalysisProgress({ phase }: { phase: number }) {
  const labels = ['读取比赛与盘口', '计算规则判向', '读取历史参照', '生成 AI 解读'];
  return <div className="analysis-progress" role="progressbar" aria-valuemin={1} aria-valuemax={4} aria-valuenow={phase} aria-busy="true"><div className="analysis-progress-head"><span>{labels[Math.max(0, phase - 1)]}</span><b>{Math.round(phase / 4 * 100)}%</b></div><div className="analysis-progress-track"><i style={{ width: Math.round(phase / 4 * 100) + '%' }} /></div><small>数据体检和四层判向完成后即可先看规则结论，AI 解读可继续异步加载。</small></div>;
}

export function AnalysisDrawer({ target, historyId: initialHistoryId = null, onClose }: { target: AnalysisTarget; historyId?: string | null; onClose: () => void }) {
  useBodyScrollLock(true);
  const requestIdRef = useRef('analysis-' + (globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2) + Date.now().toString(36)));
  const [data, setData] = useState<AnalysisPayloadDto | null>(null);
  const [historyId, setHistoryId] = useState<string | null>(initialHistoryId);
  const [aiResult, setAiResult] = useState<DecisionAiDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState(1);
  const [aiLoading, setAiLoading] = useState(false);
  const [error, setError] = useState('');
  const [aiError, setAiError] = useState('');
  const [favorite, setFavorite] = useState<boolean | null>(null);
  const [favoriteBusy, setFavoriteBusy] = useState(false);
  const runAi = async (historyForAi = historyId) => {
    setAiLoading(true); setAiError(''); setPhase(3);
    try {
      const result = await runDecisionAi(target.scheduleId, historyForAi || null);
      setAiResult(result); setPhase(4);
    } catch (caught) {
      setAiError(caught instanceof Error ? caught.message : 'AI 解读失败');
    } finally { setAiLoading(false); }
  };
  useEffect(() => {
    let alive = true;
    setLoading(true); setError(''); setPhase(initialHistoryId ? 2 : 1);
    const request = initialHistoryId
      ? getAnalysisHistoryItem(initialHistoryId).then((item) => item as AnalysisPayloadDto & { aiResult?: DecisionAiDto | null })
      : runMatchAnalysis(target.scheduleId, requestIdRef.current);
    request.then((payload) => {
      if (!alive) return;
      setData(payload);
      setHistoryId(payload.historyId ?? initialHistoryId ?? null);
      setFavorite(Boolean(payload.favorite));
      setPhase(2);
      if (initialHistoryId && (payload as any).aiResult) {
        const saved = (payload as any).aiResult as DecisionAiDto;
        setAiResult({ ...saved, agreement: String(saved.agreement ?? '') });
        setPhase(4);
      } else if (payload.ai.available) {
        void runAi(payload.historyId ?? initialHistoryId ?? null);
      }
    }).catch((caught) => { if (alive) setError(caught instanceof Error ? caught.message : '读取分析失败'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [target.scheduleId, initialHistoryId]);
  const toggleFavorite = async () => {
    setFavoriteBusy(true); setError('');
    try {
      const next = favorite === true
        ? await deleteFavorite(target.scheduleId)
        : await saveFavorite(target.scheduleId, historyId);
      setFavorite(next);
    } catch (caught) { setError(caught instanceof Error ? caught.message : '收藏操作失败'); }
    finally { setFavoriteBusy(false); }
  };
  const recommendation = data?.analysis.rule.recommendation;
  return <section className="analysis-overlay" data-no-pull-refresh role="dialog" aria-modal="true" aria-label="AI 分析" aria-busy={loading || aiLoading}>
    <header className="analysis-drawer-head"><div><span className="ai-kicker">NowScore AI 判向{initialHistoryId ? ' · 历史快照' : ''}</span><h2>{target.homeTeamName} <small>vs</small> {target.awayTeamName}</h2><p>{target.lotteryNumber ? target.lotteryNumber + ' · ' : ''}{formatKickoff(target.kickoffTime)}</p></div><div className="analysis-head-actions"><button className={favorite ? 'ghost active' : 'ghost'} disabled={favoriteBusy || favorite === null} aria-busy={favoriteBusy} onClick={() => void toggleFavorite()}>{favorite === null ? '收藏状态读取中…' : favorite ? '★ 已收藏' : '☆ 收藏'}</button><button className="close-button" onClick={onClose}>关闭</button></div></header>
    <div className="analysis-drawer-body">
      {loading && <AnalysisProgress phase={phase} />}
      {error && <div className="login-error">{error}</div>}
      {data && <>
        <AuditStrip data={data} />
        <div className="analysis-columns"><RuleColumn data={data} /><AiColumn data={data} result={aiResult} loading={aiLoading} error={aiError} onRun={() => void runAi()} /></div>
        <Dimensions data={data} />
        <References data={data} />
      </>}
    </div>
    {recommendation && <footer className={`analysis-conclusion conclusion-${recommendation.status === '可参考' ? 'ok' : recommendation.canReference ? 'warn' : 'bad'}`}><div><span>最终建议 · 规则权威</span><b>{recommendation.status} · {recommendation.primarySelection ?? '等待数据'}</b><Stars value={recommendation.stars} /></div><p>{recommendation.summary}</p><div className="conclusion-evidence">{recommendation.reasons.map((item) => <span key={item}>{item}</span>)}</div></footer>}
  </section>;
}